<template>
  <div class="whatsapp-config">
    <div class="notification-config-section-heading whatsapp-config-heading">
      <div>
        <h5>Whatsapp</h5>
        <p>Conexiones a WhatsApp Cloud API para notificaciones salientes.</p>
      </div>
      <b-button @click="add()" type="button" variant="link" class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom title="Agregar Whatsapp" aria-label="Agregar Whatsapp">
        <AppIcon name="add" />
      </b-button>
    </div>

    <b-row class="mb-3">
      <b-col lg="7" sm="12">
        <b-table class="app-table mb-0"
                responsive
                sticky-header="60vh"
                :stacked="isMobileViewport ? 'md' : false"
                :hover="true"
                :fixed="false"
                :items="table.items"
                :fields="table.fields"
                :filter="table.filter"
                :current-page="table.currentPage"
                :per-page="table.perPage"
                v-if="table.items.length">

          <template v-slot:cell(id)="data">
            <b>{{ data.item.id }}</b>
          </template>

          <template v-slot:cell(active)="data">
            <span class="notification-status" :class="data.item.active ? 'notification-status--active' : 'notification-status--inactive'">
              <span class="notification-status__dot"></span>
              {{ data.item.active ? 'Activo' : 'Inactivo' }}
            </span>
          </template>

          <template v-slot:cell(templates_count)="data">
            <span class="templates-count-value">{{ activeTemplatesCount(data.item) }}</span>
          </template>

          <template v-slot:cell(webhook)="data">
            <b-button size="sm" variant="link" class="whatsapp-webhook-btn" v-b-tooltip.hover title="Copiar URL del Webhook" @click="copyWebhookUrl(data.item.id)" aria-label="Copiar URL del Webhook">
              <AppIcon name="clipboard" />
            </b-button>
          </template>

          <template v-slot:cell(f_action)="data">
            <b-dropdown right
                        no-caret
                        size="sm"
                        variant="link"
                        boundary="window"
                        :toggle-class="isMobileViewport ? 'whatsapp-action-toggle-labeled' : 'app-table-action-toggle'">
              <template #button-content>
                <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                <span v-if="isMobileViewport">Opciones</span>
              </template>
              <b-dropdown-item @click="testConnection(data.item)" v-if="data.item.phone_number_id && data.item.access_token">
                <AppIcon name="send" class="mr-1" /> Probar Conexión
              </b-dropdown-item>
              <b-dropdown-item @click="openTemplates(data.item)" v-if="data.item.active && data.item.waba_id">
                <AppIcon name="file-earmark-text" class="mr-1" /> Plantillas
              </b-dropdown-item>
              <b-dropdown-header>Acciones</b-dropdown-header>
              <b-dropdown-item @click="edit(data.item)">
                <AppIcon name="edit" class="mr-1" /> Editar
              </b-dropdown-item>
              <b-dropdown-item @click="remove(data.item)">
                <AppIcon name="delete" class="mr-1" /> Eliminar
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <EmptyState v-else icon="chat-dots" title="No hay configuraciones" description="Agregá una conexión para habilitar envíos por Whatsapp." />

        <div class="app-table-footer whatsapp-config-table-footer" v-if="table.items.length">
          <span class="list-view-toolbar__count">{{ getRowCount(table.items) }} registros</span>
        </div>

        <nav class="whatsapp-config-pagination" v-if="table.items.length > table.perPage">
          <b-pagination class="mb-0"
                        size="sm"
                        pills
                        :total-rows="getRowCount(table.items)"
                        :per-page="table.perPage"
                        v-model="table.currentPage" />
        </nav>
      </b-col>
      <b-col lg="5" sm="12">
        <section class="notification-help-panel whatsapp-help-panel">
          <div class="notification-help-panel__header">
            <span class="notification-help-panel__icon"><AppIcon name="chat-dots" /></span>
            <div>
              <h6>Implementación</h6>
              <p>WhatsApp Cloud API oficial de Meta.</p>
            </div>
          </div>

          <p>Hay dos formas de conectarse, según si ya tenés o no una cuenta de negocio verificada en Meta.</p>

          <div class="notification-help-block">
            <h6><span class="whatsapp-mode whatsapp-mode--test">Modo prueba</span></h6>
            <ol>
              <li>Creá una App tipo <b>Business</b> en <a href="https://developers.facebook.com/apps" target="_blank">Meta Developers</a>.</li>
              <li>Agregá el producto WhatsApp y copiá el <b>Phone Number ID</b> y el <b>Temporary access token</b>.</li>
              <li>Agregá hasta 5 teléfonos verificados para recibir mensajes de prueba.</li>
            </ol>
            <div class="notification-inline-message notification-inline-message--info">
              <AppIcon name="clock-history" />
              <span>El token temporal expira a las 24hs. Para uno de 60 días, usá un usuario de sistema con permiso <code>whatsapp_business_messaging</code>.</span>
            </div>
          </div>

          <div class="notification-help-block">
            <h6><span class="whatsapp-mode whatsapp-mode--production">Modo real</span></h6>
            <ol>
              <li>Completá la verificación del negocio en Meta Business Suite.</li>
              <li>Agregá el número real que usarán tus clientes para identificarte.</li>
              <li>Cargá el token permanente y el <b>WABA ID</b> para gestionar plantillas.</li>
            </ol>
            <div class="notification-inline-message notification-inline-message--warning">
              <AppIcon name="warning" />
              <span>Fuera de una conversación iniciada por el cliente en las últimas 24hs, Meta solo permite enviar plantillas aprobadas.</span>
            </div>
          </div>

          <div class="notification-help-block">
            <h6>Webhook</h6>
            <p class="mb-0">El <b>Verify Token</b> es la palabra que también se carga en Meta al configurar el Callback URL del webhook.</p>
          </div>
        </section>
      </b-col>
    </b-row>

    <!-- ########################### -->
    <!-- #####     MODALES     ##### -->
    <!-- ########################### -->

    <b-modal v-model="modal.form.active"
            :key="modal.form._id"
            :id="modal.form._id">                
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon :name="crud.form.id ? 'edit' : 'add'" /></span>
        <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
      </div>

      <b-row>
        <b-col md="12">
          <b-form-group label="Referencia">
            <b-form-input type="text"
                          size="sm"
                          v-model="crud.form.name"
                          required>
            </b-form-input>
          </b-form-group>
        </b-col>                    
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Phone Number ID">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.phone_number_id"
                            required>
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="WABA ID" description="WhatsApp Business Account ID — necesario para dar de alta plantillas">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.waba_id">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Access Token">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.access_token"
                            required>
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Verify Token" description="Palabra clave a configurar también en el Webhook de Meta">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.verify_token"
                            required>
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="crud.form.id">
        <b-col md="12">
          <b-form-group label="Webhook URL" description="Pegala tal cual en Meta: App → WhatsApp → Configuration → Webhook → Callback URL">
            <b-input-group size="sm">
              <b-form-input type="text"
                            size="sm"
                            :value="webhookUrl(crud.form.id)"
                            readonly>
              </b-form-input>
              <b-input-group-append>
                <b-button variant="secondary" size="sm" @click="copyWebhookUrl(crud.form.id)">
                  <AppIcon name="clipboard" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col md="12">
          <div class="notification-inline-message notification-inline-message--info">
            <AppIcon name="info-circle" />
            <span>Guardá el registro para obtener la URL del Webhook.</span>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
        <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
      </div>
    </b-modal>
  
    <!-- MODAL TEST -->
    <b-modal v-model="modal.formTest.active">

      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="send" /></span>
        <h5 class="app-modal-header__title">{{this.modal.formTest.title}}</h5>
      </div>

      <b-row>
        <b-col md="12">
          <b-form-group label="Referencia">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.name"
                            disabled>
            </b-form-input>
          </b-form-group>
        </b-col>                    
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Phone Number ID">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.phone_number_id"
                            disabled>
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Access Token">
            <b-form-input type="text"
                            size="sm"
                            v-model="crud.form.access_token"
                            disabled>
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Télefono de Prueba" description="Debe ser uno de los números verificados como destinatario de prueba en tu Meta App. Podés escribirlo con o sin el 9 de móvil, lo ajustamos automáticamente.">
            <b-form-input type="number"
                          size="sm"
                          v-model="crud.form.nroTest"
                          placeholder="Cod país [sin el signo] + cod área [sin el 0] + nro cel [sin el 15]">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.formTest.active=false"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
        <b-button variant="dark" @click="testWhatsapp()"><AppIcon name="send" class="mr-1" />Probar Conexión</b-button>
      </div>
    </b-modal>

    <!-- MODAL PLANTILLAS -->
    <b-modal v-model="modal.templates.active"
            size="lg"
            no-close-on-backdrop>

      <div slot="modal-header" class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="file-earmark-text" /></span>
        <h5 class="app-modal-header__title">Plantillas - {{ modal.templates.whatsappName }}</h5>
      </div>

      <b-row class="mb-2">
        <b-col md="4">
          <b-form-group label="Filtrar por estado">
            <b-form-select v-model="table.templates.filterStatus" :options="arr.select.templateStatusFilter"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-table class="app-table app-table--compact mb-0"
              responsive
              :stacked="isMobileViewport ? 'md' : false"
              :hover="true"
              :fixed="false"
              :items="filteredTemplates"
              :fields="table.templates.fields"
              v-if="filteredTemplates.length">

        <template v-slot:cell(body)="data">
          <span :title="data.item.body">{{ data.item.body }}</span>
        </template>

        <template v-slot:cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
          <div v-if="data.item.status == 'REJECTED' && data.item.rejected_reason" class="small text-danger mt-1">
            {{ data.item.rejected_reason }}
          </div>
        </template>

        <template v-slot:cell(f_action)="data">
          <b-button size="sm" variant="dark" class="mr-1 mb-1" @click="validateTemplate(data.item)">
            <AppIcon name="send" class="mr-1" />Enviar a validar
          </b-button>
          <b-button size="sm" variant="outline-secondary" class="mb-1" @click="refreshTemplateStatus(data.item)" v-if="data.item.meta_template_id">
            <AppIcon name="refresh" class="mr-1" />Actualizar estado
          </b-button>
        </template>
      </b-table>
      <EmptyState v-else icon="file-earmark-text" title="No hay plantillas" description="No se encontraron plantillas para este filtro." />

      <div slot="modal-footer">
        <b-button variant="outline-secondary" @click="modal.templates.active=false"><AppIcon name="x" class="mr-1" />Cerrar</b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
  import serviceAPI from './../services'
  import Error from '@/handler/error'
  import Funciones from '@/handler/funciones'
  import ConfigAPI from '@/config/api'

  export default {
    data: () => {
      return {      
        table : {
          items: [],
          fields: [
            {key: 'id', label: 'ID', sortable: true, class:"align-middle"},
            {key: 'name', label: 'Referencia', class:"align-middle"},
            {key: 'phone_number_id', label: 'Phone Number ID', class:"align-middle"},
            {key: 'verify_token', label: 'Verify Token', class:"align-middle"},
            {key: 'active', label: 'Activo', class:"align-middle text-left"},
            {key: 'templates_count', label: 'Plantillas Activas', class:"align-middle text-left"},
            {key: 'webhook', label: 'Webhook', class:"align-middle text-left"},
            {key: 'f_action', label:'Accion', class:"align-middle text-left"},
          ],
          currentPage: 1,
          perPage: 50,
          templates: {
            items: [],
            filterStatus: '',
            fields: [
              {key: 'name', label: 'Nombre', class:"align-middle"},
              {key: 'category', label: 'Categoría', class:"align-middle text-left"},
              {key: 'body', label: 'Mensaje', class:"align-middle"},
              {key: 'status', label: 'Estado', class:"align-middle text-left"},
              {key: 'f_action', label:'Accion', class:"align-middle text-left"},
            ],
          },
        },
        crud: {
          form: {
            id: 0,
            name: '',
            phone_number_id: '',
            waba_id: '',
            access_token: '',
            verify_token: '',
            nroTest: '',
          },
        },
        arr: {
          select: {
            templateStatusFilter: [
              { value: '', text: 'Todas' },
              { value: 'active', text: 'Activas' },
              { value: 'inactive', text: 'No activas' },
            ],
          }
        },
        modal: {
          form: {
            active: false,
            title: ''
          },
          formTest: {
            active: false,
            title: ''
          },
          templates: {
            active: false,
            whatsappId: 0,
            whatsappName: '',
          },
        },
        isMobileViewport: false,
      }
    },
    computed: {
      filteredTemplates() {
        if(this.table.templates.filterStatus == 'active') {
          return this.table.templates.items.filter((item) => item.status == 'APPROVED')
        }
        if(this.table.templates.filterStatus == 'inactive') {
          return this.table.templates.items.filter((item) => item.status != 'APPROVED')
        }
        return this.table.templates.items
      }
    },
    mounted () {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
      this.show()
      this.loadTemplates()
    },
    beforeDestroy() {
      if(this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
    },
    methods: {
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      getRowCount (items) {
        return items.length
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      show() {        
        var result = serviceAPI.obtenerWhatsapp()

        result.then((response) => {
          var data = response.data
          this.table.items = data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        });  
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = ''
        this.crud.form.phone_number_id = ''
        this.crud.form.waba_id = ''
        this.crud.form.access_token = ''
        this.crud.form.verify_token = ''

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Whatsapp"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.phone_number_id = item.phone_number_id
        this.crud.form.waba_id = item.waba_id
        this.crud.form.access_token = item.access_token
        this.crud.form.verify_token = item.verify_token

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Whatsapp"
        this.modal.form.active = true
      },
      remove(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.phone_number_id = item.phone_number_id
        this.crud.form.access_token = item.access_token

        this.confirmDialog('¿Desea borrar el item (' + this.crud.form.id + ') - '+ this.crud.form.name + '?', {
          title: 'Borrar Whatsapp',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO',
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarWhatsapp(this.crud.form.id);

            result.then(() => {
              this.modal.form.active = false        
              loader.hide()
              this.show()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      save() {
        let loader = this.$loading.show();
        var result = null
        if (this.crud.form.id) {
          result = serviceAPI.editarWhatsapp(this.crud.form);
        } else {
          result = serviceAPI.agregarWhatsapp(this.crud.form);
        }

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.show()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      testConnection(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.phone_number_id = item.phone_number_id
        this.crud.form.access_token = item.access_token
        this.crud.form.nroTest = ''

        this.modal.formTest.title = "Probar Conexión"
        this.modal.formTest.active = true
      },
      testWhatsapp() {
        if(!Funciones.validarTelefonoWhatsapp(this.crud.form.nroTest)) {
          this.$awn.alert("No se puede realizar el test sino se ingresa un nro de teléfono válido (con o sin el 9 de móvil)")
          return false
        }
        this.confirmDialog('¿Probar configuración de whatsapp?', {
          title: 'Configuración de whatsapp',
          icon: 'send',
          okTitle: 'PROBAR',
        })
        .then(value => {
          if (value) {   
            let loader = this.$loading.show();
            var result = serviceAPI.testWhatsapp(this.crud.form)

            result.then(() => {
              this.modal.formTest.active = false
              loader.hide()
              this.show()   
              this.$awn.success('Conexión en funcionamiento!!')              
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            });
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },

      openTemplates(item) {
        this.modal.templates.whatsappId = item.id
        this.modal.templates.whatsappName = item.name
        this.table.templates.filterStatus = ''

        this.loadTemplates()
        this.modal.templates.active = true
      },
      loadTemplates() {
        var result = serviceAPI.listWhatsappTemplates()

        result.then((response) => {
          this.table.templates.items = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      statusVariant(status) {
        var map = { DRAFT: 'secondary', PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', PAUSED: 'dark', DISABLED: 'dark' }
        return map[status] || 'secondary'
      },
      validateTemplate(template) {
        this.confirmDialog('¿Enviar la plantilla "' + template.name + '" a validar con "' + this.modal.templates.whatsappName + '"?', {
          title: 'Enviar a validar',
          icon: 'send',
          okTitle: 'ENVIAR',
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.validateWhatsappTemplate(template.id, this.modal.templates.whatsappId)

            result.then(() => {
              loader.hide()
              this.loadTemplates()
              this.$awn.success("Plantilla enviada a validar")
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },
      refreshTemplateStatus(template) {
        let loader = this.$loading.show();
        var result = serviceAPI.refreshWhatsappTemplateStatus(template.id)

        result.then(() => {
          loader.hide()
          this.loadTemplates()
          this.$awn.success("Estado actualizado")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },

      activeTemplatesCount(item) {
        return this.table.templates.items.filter((template) => {
          return template.notifications_whatsapp_id == item.id && template.status == 'APPROVED'
        }).length
      },
      webhookUrl(id) {
        return ConfigAPI.baseURL + 'notifications/whatsapp/' + id + '/webhooks'
      },
      copyWebhookUrl(id) {
        var url = this.webhookUrl(id)

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url)
            .then(() => this.$awn.success('Webhook copiado al portapapeles'))
            .catch(() => this.$awn.alert('No se pudo copiar. Copialo manualmente: ' + url))
        } else {
          this.$awn.alert('No se pudo copiar. Copialo manualmente: ' + url)
        }
      },
    }
  }
</script>

<style scoped>
  .whatsapp-config {
    color: var(--app-color-text);
  }
  .whatsapp-config-heading {
    margin-bottom: 16px;
  }
  .notification-config-section-heading {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    justify-content: space-between;
  }
  .notification-config-section-heading h5 {
    color: var(--app-color-text);
    font-size: var(--app-text-lg);
    font-weight: 700;
    margin: 0;
  }
  .notification-config-section-heading p {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-sm);
    margin: 2px 0 0;
  }
  .notification-status {
    align-items: center;
    border-radius: 999px;
    display: inline-flex;
    font-size: var(--app-text-xs);
    font-weight: 700;
    gap: 6px;
    padding: 4px 9px;
    text-transform: uppercase;
  }
  .notification-status__dot {
    border-radius: 999px;
    display: inline-block;
    height: 7px;
    width: 7px;
  }
  .notification-status--active {
    background: var(--app-color-success-soft);
    color: var(--app-color-success);
  }
  .notification-status--active .notification-status__dot {
    background: var(--app-color-success);
  }
  .notification-status--inactive {
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger);
  }
  .notification-status--inactive .notification-status__dot {
    background: var(--app-color-danger);
  }
  .notification-help-panel {
    background: var(--app-color-surface-subtle);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    color: var(--app-color-text-secondary);
    font-size: var(--app-text-sm);
    padding: 14px;
  }
  .notification-help-panel p {
    margin-bottom: 10px;
  }
  .notification-help-panel ol,
  .notification-help-panel ul {
    margin-bottom: 0;
    padding-left: 18px;
  }
  .notification-help-panel li + li {
    margin-top: 5px;
  }
  .notification-help-panel__header {
    align-items: center;
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
  }
  .notification-help-panel__header h6 {
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: 700;
    margin: 0;
  }
  .notification-help-panel__header p {
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    margin: 1px 0 0;
  }
  .notification-help-panel__icon {
    align-items: center;
    background: var(--app-color-primary-soft);
    border: 1px solid var(--app-color-primary-200);
    border-radius: 8px;
    color: var(--app-color-primary);
    display: inline-flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;
  }
  .notification-help-block {
    border-top: 1px solid var(--app-color-border);
    margin-top: 12px;
    padding-top: 12px;
  }
  .notification-help-block h6 {
    color: var(--app-color-text);
    font-size: var(--app-text-xs);
    font-weight: 700;
    margin-bottom: 6px;
    text-transform: uppercase;
  }
  .notification-inline-message {
    align-items: flex-start;
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    display: flex;
    font-size: var(--app-text-sm);
    gap: 8px;
    padding: 10px 12px;
  }
  .notification-inline-message--info {
    background: var(--app-color-info-soft);
    border-color: var(--app-color-info-200);
    color: var(--app-color-info);
  }
  .notification-inline-message--warning {
    background: var(--app-color-warning-soft);
    border-color: var(--app-color-warning-200);
    color: var(--app-color-warning);
    margin-top: 12px;
  }
  .whatsapp-mode {
    border-radius: 999px;
    display: inline-flex;
    padding: 3px 8px;
  }
  .whatsapp-mode--test {
    background: var(--app-color-info-soft);
    color: var(--app-color-info);
  }
  .whatsapp-mode--production {
    background: var(--app-color-success-soft);
    color: var(--app-color-success);
  }
  .whatsapp-webhook-btn {
    align-items: center;
    color: var(--app-color-text-secondary);
    display: inline-flex;
    justify-content: center;
    min-height: 28px;
    min-width: 28px;
    padding: 0;
  }
  .whatsapp-webhook-btn:hover,
  .whatsapp-webhook-btn:focus {
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text);
  }
  .btn.whatsapp-action-toggle-labeled {
    align-items: center;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    display: inline-flex !important;
    font-size: var(--app-text-sm);
    height: auto !important;
    justify-content: flex-start;
    line-height: 1.15;
    min-height: 0;
    padding: 0 !important;
    width: auto !important;
  }
  .btn.whatsapp-action-toggle-labeled:hover,
  .btn.whatsapp-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  .whatsapp-config-table-footer {
    padding-right: 12px;
  }
  .whatsapp-config-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }
  .templates-count-value {
    color: var(--app-color-text);
    font-size: var(--app-text-lg);
    font-weight: 700;
  }
  @media (max-width: 767.98px) {
    .notification-config-section-heading {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }
    .notification-help-panel {
      margin-top: 12px;
    }
    .whatsapp-config-pagination {
      justify-content: flex-start;
    }
    .app-table.b-table-stacked-md > tbody > tr > td:last-child {
      text-align: left !important;
    }
    .whatsapp-webhook-btn {
      justify-content: flex-start;
    }
  }
</style>
